import BaseController from 'app/src/controllers/base/BaseController';
import ButtonViewController from 'app/src/controllers/components/ButtonViewController';
import DetailView from 'app/src/views/components/DetailView';
import DetailHeaderView from 'app/src/views/components/DetailHeaderView';
import Localize from 'app/src/localize/Localize';
import _ from 'underscore';

const SAVE_BUTTON_DEFAULT_TEXT = Localize.translate(Localize.translate('Save'));
const SAVE_BUTTON_DEFAULT_IN_PROGRESS_TEXT = Localize.translate(Localize.translate('Saving...'));
const SAVE_BUTTON_DEFAULT_SAVED_TEXT = Localize.translate(Localize.translate('Saved!'));

export default class DetailViewController extends BaseController {
  /**
   * Constructor for a DetailViewController object which is injected with a view
   * which is displayed within its content region.  This type of view will present
   * a save and delete button with appropriate hooks so that the class may be extended
   * to provide the specific implementation.
   *
   * @param {*} detailView    The view to be displayed in the content region
   * @param {Object} options  Configuration options for the view controller.
   * @param {Object} options.save         Configuration options for the save button.
   * @param {string} options.save.text    Save button text
   * @param {string} options.save.inProgressText  Text for the in progress button state
   * @param {string} options.save.savedText       Text for the done state (held for 1 second)
   *
   */
  constructor(detailView, options = {}) {
    if (!detailView) {
      throw 'A view must be specified when creating a DetailViewController';
    }

    super();

    this._detailView = detailView;
    this._options = options;
    this._options.save = options.save || {};
    this._buttons = [];

    this.view = new DetailView();
  }

  /**
   * Called to show the view.  Renders the injected child view into the
   * content region.
   */
  show() {
    super.show();

    this.setHeaderView(new DetailHeaderView());
    this.showChildView('content', this._detailView);

    this.listenTo(this._detailView, 'all', this._onEvent);
  }

  /**
   * Adds a button to a list of buttons supported by the detail view.
   *
   * @param {ButtonViewController} button     The controller for the button to be added
   */
  addButton(button) {
    this._buttons.push(button);
    button.show();

    this.view.addButton(button.view.$el);
  }

  /**
   * Retreives a registered button.
   *
   * @param {string} name     The name of the button (specified when the button is constructed)
   */
  getButton(name) {
    return _.findWhere(this._buttons, { name });
  }

  /**
   * Performs any necessary cleanup when the controller is destroyed.  Cleans up any constructed
   * buttons in addition to the normal cleanup operations.
   */
  destroy() {
    super.destroy();

    _.each(this._buttons, buttonController => {
      buttonController.destroy();
    });
    this._buttons = [];
  }

  /**
   * Set the header view for the detail view.  Any buttons added to the header will
   * automatically be removed and cleaned up.
   *
   * @param {DetailView} headerView       The details are s
   * @param buttons
   */
  setHeaderView(headerView = new DetailHeaderView(), buttons = []) {
    _.each(this._buttons, buttonController => {
      buttonController.destroy();
    });
    this._buttons = [];

    this.showChildView('header', headerView);

    if (buttons.length) {
      _.each(buttons, buttonController => {
        this.addButton(buttonController);
      });
    } else {
      this._addDefaultButtons();
    }
  }

  /**
   * Retrieves the header view
   *
   * @returns {*}
   */
  getHeaderView() {
    return this.view.getRegion('header').currentView;
  }

  /**
   * Adds default buttons to the header
   *
   * @private
   */
  _addDefaultButtons() {
    this.addButton(
      new ButtonViewController({
        name: 'save',
        type: ButtonViewController.ButtonType.PRIMARY,
        text: this._options.save.text || SAVE_BUTTON_DEFAULT_TEXT,
        inProgressText: this._options.save.inProgressText || SAVE_BUTTON_DEFAULT_IN_PROGRESS_TEXT,
        doneMessage: this._options.save.savedText || SAVE_BUTTON_DEFAULT_SAVED_TEXT,
        actionDelegate: this.save ? this.save.bind(this) : null,
      })
    );

    this.addButton(
      new ButtonViewController({
        name: 'delete',
        type: ButtonViewController.ButtonType.DANGER,
        text: 'Delete',
        actionDelegate: this.remove ? this.remove.bind(this) : null,
      })
    );

    this.getButton('delete').view.show(!this._options.isNew);
  }

  /**
   * Disables the detail section
   *
   * @param {boolean} disable     When true, the details section is disabled
   */
  disableDetails(disable) {
    this.view.disableDetailView(disable);
  }

  /**
   * Propagates detail view events
   *
   * @param {arguments} args  The arguments passed with the event
   * @private
   */
  _onEvent(...args) {
    // eslint-disable-next-line prefer-spread
    this.triggerMethod.apply(this, args);
  }
}
